/*
 *
 * App
 *
 */

import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { selectAuthToken, selectIsPhotographer, selectTrialDaysRemaining } from './selectors';
import { selectIsSuperAdmin } from 'containers/AdminPage/selectors';
import { useFeatureFlag } from 'utils/posthog';
import { bootThirdParties, isPublicGuestRoute } from 'utils/thirdParty';
import { signOut } from './actions';
import Wrapper from './Wrapper';

import RootModal from 'containers/RootModal';
import LeftMenu from 'components/LeftMenu';
import TopMenu from 'components/TopMenu';
import GetStartedMenu from 'components/GetStartedMenu';
import TrialCountdownBanner from 'components/TrialCountdownBanner';
import Intercom from "components/Intercom";
import ErrorBoundary from "components/ErrorBoundary";
import { Toaster } from 'sonner';
import { useLocation } from "react-router-dom";

const structuredSelector = createStructuredSelector({
  authToken: selectAuthToken(),
  isPhotographer: selectIsPhotographer(),
  isSuperAdmin: selectIsSuperAdmin(),
  trialDaysRemaining: selectTrialDaysRemaining(),
});

const App = ({ switchComponent: SwitchComponent }) => {
  const dispatch = useDispatch();
  const { authToken, isPhotographer, isSuperAdmin, trialDaysRemaining } =
    useSelector(structuredSelector);
  const superadminEnabled = useFeatureFlag('react_superadmin_enabled');
  const location = useLocation();
  const mainAppRef = useRef(null);

  // Show the Admin nav link only when the section is reachable: the user is a
  // super_admin AND the react_superadmin_enabled flag is on. Mirrors the gate
  // AdminPage applies to the route itself, so the link never points at a
  // surface that would just redirect away.
  const showAdminLink = superadminEnabled && isSuperAdmin;

  useEffect(() => {
    if (mainAppRef.current) {
      mainAppRef.current.scrollTop = 0;
    }
  }, [location]);

  const handleSignOut = () => dispatch(signOut());

  // The chrome-less public routes (galleries, share redirects, download
  // landing) — the same set that decides which third parties may boot, so the
  // two never drift apart. See utils/thirdParty.
  const isBlankPage = isPublicGuestRoute(location.pathname);

  // Re-run the route-keyed third-party decision on navigation, so a visitor who
  // lands on a public gallery and later reaches an app route still gets what
  // that route needs. Every installer is idempotent, so this is a no-op once
  // the landing-route boot in src/index.js has already run.
  useEffect(() => {
    bootThirdParties(location.pathname);
  }, [location.pathname]);

  return (
    <Wrapper id="main-app" ref={mainAppRef}>
      <Toaster position="bottom-right" richColors />
      <RootModal />
      {authToken && <Intercom authToken={authToken} />}
      <ErrorBoundary>
        {!isBlankPage ? (
          authToken ? (
            <LeftMenu
              isPhotographer={isPhotographer}
              showAdminLink={showAdminLink}
              signOut={handleSignOut}
            >
              <TopMenu>
                {/*
                  * Inside TopMenu's content column and above the routed page,
                  * so it sits with the content a customer is reading rather
                  * than in the chrome they navigate past — and so it appears
                  * on every authed route instead of only the one they happen
                  * to open. Renders nothing unless the account is trialing.
                  */}
                <TrialCountdownBanner daysRemaining={trialDaysRemaining} />
                <SwitchComponent authenticated={true} />
              </TopMenu>
            </LeftMenu>
          ) : (
            <GetStartedMenu>
              <SwitchComponent authenticated={false} />
            </GetStartedMenu>
          )
        ) : (
          <SwitchComponent authenticated={false} />
        )}
      </ErrorBoundary>
    </Wrapper>
  );
};

export default App;
